import React, { useState, useEffect } from 'react';
import {
  Home,
  BookOpen,
  Users,
  Image,
  MapPin,
  Search,
  Globe,
  Database,
  Shield,
  Menu,
  X,
  Hammer,
  SquareLibrary,
  Footprints,
  Sun,
  Moon,
  MessageCircle
} from 'lucide-react';


import HomePage from './pages/Home';
import Contents from './templates/Contents';
import SOCMINT from './pages/SOCMINT';
import SOCMINTTakeaways from './pages/SOCMINTTakeaways';
import HUMINT from './pages/HUMINT';
import IMINT from './pages/IMINT';
import CYBINT from './pages/CYBINT';
import GEOINT from './pages/GEOINT';
import FININT from './pages/FININT';
import DARKINT from './pages/DARKINT';
import WEAPINT from './pages/WEAPINT';
import Walkthrough from './pages/Walkthrough';
import Tools from './pages/Tools';
import Aggregation from './pages/Aggregation';
import KnowledgeGraphSidebar from './components/KnowledgeGraphSidebar';
import DRAW from './pages/DRAW';

// ==================== SIDEBAR ====================
interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPage, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => (localStorage.getItem('theme') as 'light' | 'dark') || 'light'
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const sections = [
    { id: 'home', title: 'Home', icon: Home },
    { id: 'contents', title: 'Contents', icon: BookOpen },
    { id: 'tools', title: 'Tools & Technqiues', icon: Hammer }, // DORKING
    { id: 'socmint', title: 'SOCMINT', icon: Users, indent: true },
    // { id: 'socmint-takeaways', title: '└ Key Takeaways', icon: CheckCircle, indent: true },
    { id: 'humint', title: 'HUMINT', icon: MessageCircle, indent: true },
    { id: 'imint', title: 'IMINT', icon: Image, indent: true },
    // { id: 'imint-takeaways', title: '└ Key Takeaways', icon: CheckCircle, indent: true },
    { id: 'cybint', title: 'CYBINT', icon: Shield, indent: true },
    // { id: 'cybint-takeaways', title: '└ Key Takeaways', icon: CheckCircle, indent: true },
    { id: 'geoint', title: 'GEOINT', icon: MapPin, indent: true },
    // { id: 'geoint-takeaways', title: '└ Key Takeaways', icon: CheckCircle, indent: true },
    { id: 'finint', title: 'FININT', icon: Database, indent: true },
    // { id: 'finint-takeaways', title: '└ Key Takeaways', icon: CheckCircle, indent: true },
    { id: 'darkint', title: 'DARKINT', icon: Globe, indent: true },
    // { id: 'darkint-takeaways', title: '└ Key Takeaways', icon: CheckCircle, indent: true },
    { id: 'weapint', title: 'WEAPINT', icon: Search, indent: true },
    // { id: 'weapint-takeaways', title: '└ Key Takeaways', icon: CheckCircle, indent: true },
    // { id: 'websiteHelper', title: 'Website Scrapers', icon: LayoutPanelTop }, //https://osintframework.com/
    { id: 'walkthrough', title: 'OSINT Walkthrough', icon: Footprints },
    { id: 'aggregation', title: 'Data Aggregation', icon: SquareLibrary }, //https://osintframework.com/
    { id: 'draw', title: 'Evidence Board', icon: SquareLibrary }, 
  ];

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 bg-slate-50 dark:bg-slate-800 text-white rounded-lg"
      >
        {isOpen ? <X /> : <Menu />}
      </button>

      <div className={`fixed left-0 top-0 h-screen w-72 bg-slate-50 dark:bg-slate-800 text-white z-40 transition-transform border-r dark:border-slate-700 ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h2 className="font-bold text-black dark:text-slate-100">OSINT Guide</h2>
              <p className="text-xs text-black dark:text-slate-400">Josh Holtz</p>
            </div>
          </div>

          <nav className="space-y-1">
            <button
              onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
              className="text-black dark:text-slate-100 w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700 mb-1"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </button>
            {sections.map((s) => {
              const Icon = s.icon;
              const active = currentPage === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => onNavigate(s.id)}
                  className={`text-black dark:text-slate-100 w-full flex items-center gap-3 px-4 py-3 rounded-lg ${
                    active ? 'bg-orange-500 dark:text-white' : 'hover:bg-slate-300 dark:hover:bg-slate-700'
                  } ${s.indent ? 'pl-8 text-sm' : ''}`}
                >
                  <Icon className="w-5 h-5" />
                  {s.title}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

// ==================== RIGHT SIDEBAR (REPLACED WITH KNOWLEDGE GRAPH) ====================
// Using KnowledgeGraphSidebar component instead of simple progress list

// ==================== APP ====================
const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [completedSections, setCompletedSections] = useState<string[]>([]);

  const toggleComplete = (id: string) => {
    setCompletedSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen justify-center items-center flex bg-white dark:bg-slate-900">
      <Sidebar currentPage={currentPage} onNavigate={setCurrentPage} />

      <main className="ml-0 lg:ml-72 mr-0 xl:mr-80">
        {currentPage === 'home' && <HomePage onNavigate={setCurrentPage} />}
        {currentPage === 'contents' && <Contents onNavigate={setCurrentPage} />}
        {currentPage === 'tools' && <Tools />}
        {currentPage === 'aggregation' && <Aggregation />}
        {currentPage === 'socmint' && <SOCMINT />}
        {currentPage === 'socmint-takeaways' && <SOCMINTTakeaways />}
        {currentPage === 'humint' && <HUMINT />}
        {currentPage === 'cybint' && <CYBINT />}
        {currentPage === 'imint' && <IMINT />}
        {currentPage === 'geoint' && <GEOINT />}
        {currentPage === 'finint' && <FININT />}
        {currentPage === 'darkint' && <DARKINT />}
        {currentPage === 'weapint' && <WEAPINT />}
        {currentPage === 'walkthrough' && <Walkthrough />}
        {currentPage === 'draw' && <DRAW />}
      </main>

      <KnowledgeGraphSidebar
        currentPage={currentPage}
        completedSections={completedSections}
        onNavigate={setCurrentPage}
        onToggleComplete={toggleComplete}
      />
    </div>
  );
};

export default App;
